import { type CurrencyCode } from '@omnea-app/packages/universal/currency-utils/getCurrencyCodes'
import { type BreakdownQuestionType } from './breakdownQuestionTypes'
import { NUMERIC_BUCKET_WIDTHS } from './bucketing'
import { type InsightsFilter } from './cubeQuery'
import { decodeCustomBreakdown, isCustomBreakdownKey } from './customBreakdown'
import {
  type DateBucketingState,
  type DateGrouping,
  oneStepCoarser,
  resolveDateGrouping,
  type ResolvedDateBucket,
  spanAutoGranularity,
} from './dateBucketing'
import { type DateFilterOperands, dateFilterOperands } from './dateFilter'
import { dayAfter } from './dateFormatters'
import { type FilterOperator } from './filterOperators'
import { DEFAULT_GRANULARITY, type TimeGranularity } from './granularity'
import { type BreakdownConfig, type FormAnswerBaseMembers, type FormAnswerMembers } from './insightsTypes'
import { numericBucketField } from './numericBucketField'
import {
  type BucketOverflowSide,
  type NumericBucketField,
  numericBucketOverflowFilter,
  numericBucketQuery,
  toDecimalString,
} from './numericBucketing'
import { type NumericFilterOperands, numericFilterOperands } from './numericFilter'
import { type BreakdownBucketEntry, numericBucketEntry, savedDateGranularity, type WidgetFilter } from './widgetConfig'

/**
 * Turns custom breakdown/filter keys (imported form questions, optionally at a path level — the
 * key's 4th part) into Cube dimensions and filters. Everything goes through resolveWidgetQuery.
 *
 * The view reaches the answer index through aliased copies of one hasMany join — the base
 * form_answer_* path, breakdown slots, filter slots. A joined row is one answer (one question, one
 * value, its ancestor path as per-level columns), and every join used must be pinned to its
 * question by identity filters, or all questions' answers pool together.
 *
 * Three rules, from two facts — joins sample answer rows independently, and a filter join projects
 * nothing (it can only shrink the population):
 *   R1 — everything DISPLAYED for one question reads from one join, or buckets would pair levels
 *        from different answer rows ("Asia › Spain"). Levels are extra dimensions on that join, so
 *        breakdown capacity is spent per question, not per key.
 *   R2 — every custom filter is an existence check on its own slot: ANDs across a request's
 *        answers, symmetric in row order, cannot corrupt a display.
 *   R3 — a filter on a DISPLAYED question constrains the displayed join instead (first come per
 *        column), or answers it excludes would still feed buckets (an L3 breakdown with an
 *        L1=Europe filter must never show a Tokyo bucket). A repeat of a taken column falls back
 *        to R2 — the one order-sensitive residual; every order-free alternative is worse.
 *
 * A key that cannot resolve (unknown type, no slot left, a level the members can't express) is
 * dropped whole: a missing dimension is visible, a wrong one is not.
 *
 * Reading the numbers: no Unknown bucket (the question pin drops non-answers); a multi-select puts
 * one request in several buckets; answers arrive via an hourly ETL, so new requests lag. Requests
 * only — reporting_request_steps_view exposes no form-answer members.
 */

/**
 * The question types an imported dimension can have. The available-breakdowns API's guard reads the
 * same list, so the table below cannot silently miss one — adding a type there fails the build here
 * until it is given a value member.
 */
export type CustomBreakdownType = BreakdownQuestionType

type FilterMember = 'value' | 'valueBool' | 'optionId'

// A number question's numeric member and a date question's date member are compared by operator, never
// listed as values to pick from, so they stay out of FilterMember and are reachable only through a `compare` rule.
type BreakdownMember = FilterMember | 'valueNumeric' | 'valueDate'

type FilterRule = { kind: 'in'; member: FilterMember } | { kind: 'compare'; member: 'valueNumeric' | 'valueDate' }

type BreakdownTypeRule = {
  /** The form-answer member holding this type's bucket key; null = filter-only, never a dimension. */
  member: BreakdownMember | null
  /**
   * How the type filters; null = not filterable. `in` keeps requests that answered ANY picked value;
   * `compare` keeps requests with ANY answer satisfying the widget filter's operator.
   */
  filter: FilterRule | null
  labelKey: string
}

const BREAKDOWN_TYPES: Record<CustomBreakdownType, BreakdownTypeRule> = {
  dropdown: { member: 'value', filter: { kind: 'in', member: 'value' }, labelKey: 'widget.add-breakdown.type-single-select' },
  radioGroup: { member: 'value', filter: { kind: 'in', member: 'value' }, labelKey: 'widget.add-breakdown.type-single-select' },
  dropdownMultiple: {
    member: 'value',
    filter: { kind: 'in', member: 'value' },
    labelKey: 'widget.add-breakdown.type-multi-select',
  },
  checkbox: { member: 'value', filter: { kind: 'in', member: 'value' }, labelKey: 'widget.add-breakdown.type-multi-select' },
  // These three index one row per selected option id, and value_text is a submission-time label two
  // distinct options can share. Keying on the id keeps buckets and filters distinct and survives a rename.
  'dynamic-option': {
    member: 'optionId',
    filter: { kind: 'in', member: 'optionId' },
    labelKey: 'widget.add-breakdown.type-core-data',
  },
  singleChoiceCustomField: {
    member: 'optionId',
    filter: { kind: 'in', member: 'optionId' },
    labelKey: 'widget.add-breakdown.type-single-select',
  },
  multipleChoiceCustomField: {
    member: 'optionId',
    filter: { kind: 'in', member: 'optionId' },
    labelKey: 'widget.add-breakdown.type-multi-select',
  },
  country: { member: 'value', filter: { kind: 'in', member: 'value' }, labelKey: 'widget.add-breakdown.type-country' },
  countryMultiple: {
    member: 'value',
    filter: { kind: 'in', member: 'value' },
    labelKey: 'widget.add-breakdown.type-country-multiple',
  },
  boolean: { member: 'valueBool', filter: { kind: 'in', member: 'valueBool' }, labelKey: 'widget.add-breakdown.type-boolean' },
  number: {
    member: 'valueNumeric',
    filter: { kind: 'compare', member: 'valueNumeric' },
    labelKey: 'widget.add-breakdown.type-number',
  },
  date: {
    member: 'valueDate',
    filter: { kind: 'compare', member: 'valueDate' },
    labelKey: 'widget.add-breakdown.type-date',
  },
}

export type CustomBreakdown = {
  questionName: string
  questionType: CustomBreakdownType
  sourceId: string | null
  valueMember: BreakdownMember
  /** 1-based path level of a multi-level question; the dimension is then a path-level member. */
  level?: number
}

export type DimensionFilter = { dimension: string; operator: 'equals'; values: string[] }

export type ResolvedCustomBreakdown = {
  breakdown: CustomBreakdown
  /** Where this breakdown sits among the query's `dimensions`. */
  dimensionIndex: number
  /** The join path it resolved onto: the base form-answer members or a breakdown slot's. */
  members: FormAnswerMembers
}

export type ResolvedBucketedDimension = {
  /** The breakdown key it resolved from — what `breakdownBuckets` is keyed by. */
  key: string
  /** Where the bucket dimension sits among the query's `dimensions`. */
  dimensionIndex: number
  field: NumericBucketField
  unit: CurrencyCode | null
  w: number
  min?: number
  max?: number
  /**
   * The bars' own conditions on the raw member. They are kept out of `filters` because the probe
   * that discovers the data span reuses `filters`, and bounding it by the range it is measuring
   * would pin dataMin/dataMax to the current Min/Max. Consumers compose the two lists themselves.
   */
  barFilters: InsightsFilter[]
}

export type OverflowQuery = { dimensions: string[]; filters: InsightsFilter[] }

export type ResolvedBucketOverflow = { under: OverflowQuery | null; over: OverflowQuery | null }

export type ResolvedWidgetQuery = {
  dimensions: string[]
  /** Cube-ready conditions: standard filters, each join's question pins, and custom value conditions. */
  filters: InsightsFilter[]
  /** The breakdown keys of filters that asked to narrow but placed no condition. */
  unplacedFilters: string[]
  /** The custom breakdowns among `dimensions`, in dimension order — drives labelling. */
  customBreakdowns: ResolvedCustomBreakdown[]
  /** The one numerically bucketed dimension among `dimensions`; null when the query has none. */
  bucketedDimension: ResolvedBucketedDimension | null
  /** The date breakdowns among `dimensions`, each a dotted `member.granularity` that Cube moves into timeDimensions. */
  dateBuckets: ResolvedDateBucket[]
  /**
   * What each queried date breakdown groups by, keyed by breakdown key, with the floors that could
   * coarsen it — the row's grouping menu reads it.
   */
  dateGroupings: Record<string, DateGrouping>
  /** One aggregate per bounded side of the range for the records it excludes; null when it bounds neither side. */
  overflow: ResolvedBucketOverflow | null
}

export type BucketingState = {
  enabled: boolean
  entries: Record<string, BreakdownBucketEntry>
  /**
   * The org's default currency — what a numeric core breakdown's values are denominated in. Null
   * until the org settings have loaded, so a label is never drawn in the global fallback currency.
   */
  currency: CurrencyCode | null
  /** Decides a date breakdown's granularity; read only while `enabled`. */
  dates: DateBucketingState
}

export const BUCKETING_OFF: BucketingState = {
  enabled: false,
  entries: {},
  currency: null,
  dates: {
    rangeGranularity: DEFAULT_GRANULARITY,
    rangeFloor: DEFAULT_GRANULARITY,
    spanFloorByMember: {},
  },
}

const RENDER_TIME_DEFAULT_WIDTH = Math.max(...NUMERIC_BUCKET_WIDTHS)

type NumericBucketResolution = { dimension: string; bucketed: Omit<ResolvedBucketedDimension, 'key' | 'dimensionIndex'> }

/**
 * The bucket dimension a numeric breakdown groups by, paired with the bars' own conditions. A
 * breakdown with no saved grouping falls to the coarsest ladder stop and no bounds, so it renders as
 * a handful of wide buckets rather than not at all.
 */
const resolveNumericBucket = (
  field: NumericBucketField,
  saved: BreakdownBucketEntry | undefined,
  unit: CurrencyCode | null
): NumericBucketResolution => {
  const entry = numericBucketEntry(saved)
  const grouping = entry ? { w: entry.interval, min: entry.min, max: entry.max } : { w: RENDER_TIME_DEFAULT_WIDTH }
  const bars = numericBucketQuery(field, grouping)
  return { dimension: bars.dimension, bucketed: { field, unit, ...grouping, barFilters: bars.filters } }
}

export const coreBreakdownFor = (
  key: string,
  breakdowns: Record<string, BreakdownConfig> | undefined
): BreakdownConfig | undefined => breakdowns && Object.values(breakdowns).find((config) => config.sourceKey === key)

/**
 * Cube has no member for "outside the range", so each side is its own aggregate: the bucket dimension
 * removed, every other dimension and the shared filters kept, and one inverted bound on the raw
 * member. The shared filters, not the bars', so the bound is the only condition on that member.
 */
const resolveBucketOverflow = (
  dimensions: string[],
  filters: InsightsFilter[],
  bucketed: ResolvedBucketedDimension | null
): ResolvedBucketOverflow | null => {
  if (bucketed == null || (bucketed.min == null && bucketed.max == null)) return null
  const otherDimensions = dimensions.filter((_, index) => index !== bucketed.dimensionIndex)
  const side = (overflowSide: BucketOverflowSide, bound: number | undefined): OverflowQuery | null =>
    bound == null
      ? null
      : {
          dimensions: otherDimensions,
          filters: [...filters, numericBucketOverflowFilter(bucketed.field, overflowSide, bound)],
        }
  return { under: side('under', bucketed.min), over: side('over', bucketed.max) }
}

const isCustomBreakdownType = (questionType: string): questionType is CustomBreakdownType => questionType in BREAKDOWN_TYPES

export type CustomQuestionRef = { questionName: string; questionType: CustomBreakdownType; sourceId: string | null }

const parseCustomQuestion = (key: string): (CustomQuestionRef & { level?: number }) | null => {
  const ref = decodeCustomBreakdown(key)
  if (!ref || !isCustomBreakdownType(ref.questionType)) return null
  // Only dynamic-option answers store a path for a level to index into.
  if (ref.level != null && ref.questionType !== 'dynamic-option') return null
  return {
    questionName: ref.questionName,
    questionType: ref.questionType,
    sourceId: ref.sourceId,
    ...(ref.level == null ? {} : { level: ref.level }),
  }
}

/** One join path per distinct question — levels of one question share it (see the module doc). */
const questionKey = (question: CustomQuestionRef): string =>
  `${question.questionName}::${question.questionType}::${question.sourceId}`

/** The column a filter compares on its question's join: one of the levels, or the leaf value. */
const columnKey = (level: number | undefined): string => (level == null ? 'leaf' : `level:${level}`)

/**
 * The member on `members` holding a breakdown's bucket key or a filter's compared value: the named
 * member for a flat question, the level's option-id column for a levelled one. Null when the path
 * cannot express the level — members without pathLevels, or a level past the modelled depth —
 * so the caller drops the key instead of silently grouping by the leaf.
 */
const resolveValueMember = (members: FormAnswerMembers, member: BreakdownMember, level: number | undefined): string | null =>
  level == null ? members[member] : (members.pathLevels?.[level - 1]?.optionId ?? null)

/**
 * Decode a breakdown key into the question it names, or null when it isn't a custom breakdown, its
 * type is unrecognised, or its type is filter-only. Failing closed matters: routing an unmapped
 * type to the text column would group every row under a single empty bucket.
 */
const parseCustomBreakdown = (key: string): CustomBreakdown | null => {
  const ref = parseCustomQuestion(key)
  if (!ref) return null
  const { member } = BREAKDOWN_TYPES[ref.questionType]
  if (member == null) return null
  return { ...ref, valueMember: member }
}

type CustomFilter = CustomQuestionRef & { rule: FilterRule; level?: number }

const parseCustomFilter = (key: string): CustomFilter | null => {
  const ref = parseCustomQuestion(key)
  if (!ref) return null
  const rule = BREAKDOWN_TYPES[ref.questionType].filter
  if (rule == null) return null
  return { ...ref, rule }
}

const numericConditions = (dimension: string, operator: FilterOperator, operands: NumericFilterOperands): InsightsFilter[] => {
  const low = toDecimalString(operands.low)
  const high = toDecimalString(operands.high)
  switch (operator) {
    case 'between':
      return [
        { dimension, operator: 'gte', values: [low] },
        { dimension, operator: 'lte', values: [high] },
      ]
    case 'notBetween':
      return [
        {
          or: [
            { dimension, operator: 'lt', values: [low] },
            { dimension, operator: 'gt', values: [high] },
          ],
        },
      ]
    // Cube's notEquals also matches NULL; a request with no value was never "not equal to" anything.
    case 'notEquals':
      return [
        { dimension, operator: 'set' },
        { dimension, operator: 'notEquals', values: [low] },
      ]
    case 'equals':
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
      return [{ dimension, operator, values: [low] }]
  }
}

// Cube reads a bare day as its first instant and a range's end as that day's last, so the bounds that
// must keep or drop a whole day sit at the start of the following one.
const dateConditions = (dimension: string, operator: FilterOperator, { low, high }: DateFilterOperands): InsightsFilter[] => {
  switch (operator) {
    case 'equals':
      return [{ dimension, operator: 'inDateRange', values: [low, low] }]
    case 'notEquals':
      return [{ dimension, operator: 'notInDateRange', values: [low, low] }]
    case 'between':
      return [{ dimension, operator: 'inDateRange', values: [low, high] }]
    case 'notBetween':
      return [{ dimension, operator: 'notInDateRange', values: [low, high] }]
    case 'gt':
      return [{ dimension, operator: 'afterOrOnDate', values: [dayAfter(low)] }]
    case 'gte':
      return [{ dimension, operator: 'afterOrOnDate', values: [low] }]
    case 'lt':
      return [{ dimension, operator: 'beforeDate', values: [low] }]
    case 'lte':
      return [{ dimension, operator: 'beforeDate', values: [dayAfter(low)] }]
  }
}

/** How a filter row's value input behaves: a compared number, a compared date, or a picked value list. */
export type FilterInputKind = { kind: 'number'; unit: CurrencyCode | null } | { kind: 'date' } | { kind: 'values' }

type FilterComparison = 'number' | 'date'

/** How a stored filter key compares by operator, or null when it filters by picking values. */
const filterComparison = (key: string, breakdowns: Record<string, BreakdownConfig> | undefined): FilterComparison | null => {
  if (isNumericBreakdownKey(key, breakdowns)) return 'number'
  // Ungated: a saved date filter keeps applying with bucketing off, as a saved numeric one does.
  if (isDateBreakdownKey(key, breakdowns)) return 'date'
  return null
}

/** A numeric filter's values are typed in the org currency for a core field, and in no unit for an imported number question. */
export const filterInputKind = (
  key: string,
  breakdowns: Record<string, BreakdownConfig> | undefined,
  currency: CurrencyCode | null
): FilterInputKind => {
  switch (filterComparison(key, breakdowns)) {
    case 'number':
      return { kind: 'number', unit: isCustomBreakdownKey(key) ? null : currency }
    case 'date':
      return { kind: 'date' }
    case null:
      return { kind: 'values' }
  }
}

/**
 * A filter's conditions on `dimension`, or null while it narrows nothing: no values picked, or a
 * compared filter still missing a value. An operator on a key that doesn't compare by one, or the
 * reverse, is dropped rather than read as the other kind.
 */
const valueConditions = (
  dimension: string,
  filter: WidgetFilter,
  comparison: FilterComparison | null
): InsightsFilter[] | null => {
  if (filter.operator == null) {
    // null (all) and [] (none) both mean "no narrowing"; only a partial selection filters.
    if (comparison != null || filter.values == null || filter.values.length === 0) return null
    return [{ dimension, operator: 'equals', values: filter.values }]
  }
  if (comparison === 'number') {
    const operands = numericFilterOperands(filter.operator, filter.values)
    return operands == null ? null : numericConditions(dimension, filter.operator, operands)
  }
  if (comparison === 'date') {
    const operands = dateFilterOperands(filter.operator, filter.values)
    return operands == null ? null : dateConditions(dimension, filter.operator, operands)
  }
  return null
}

export const identityFiltersFor = (question: CustomQuestionRef, formAnswers: FormAnswerMembers): DimensionFilter[] => {
  const filters: DimensionFilter[] = [
    { dimension: formAnswers.question, operator: 'equals', values: [question.questionName] },
    { dimension: formAnswers.answerType, operator: 'equals', values: [question.questionType] },
  ]
  if (question.sourceId != null) {
    filters.push({ dimension: formAnswers.dataSource, operator: 'equals', values: [question.sourceId] })
  }
  return filters
}

/**
 * Expand a widget's saved breakdowns and filters into the dimensions and filters its Cube queries
 * need, applying R1–R3 (module doc). Standard keys and filters pass through untouched; a custom key
 * becomes its value member on its question's join plus the identity filters pinning that join.
 *
 * Numeric keys — a `number` question or a core breakdown with `type: 'number'` — group by a bucket
 * dimension instead. The query describes at most one, so a second numeric key is dropped whole.
 *
 * Date keys — a core breakdown with `type: 'time'` or a `date` question — group by the dotted
 * `member.granularity`, which Cube moves into timeDimensions.
 */
export const resolveWidgetQuery = ({
  breakdownKeys,
  widgetFilters,
  formAnswers,
  breakdownSlots,
  filterSlots,
  breakdowns,
  bucketing,
}: {
  breakdownKeys: string[]
  widgetFilters: WidgetFilter[]
  formAnswers?: FormAnswerMembers
  breakdownSlots?: FormAnswerMembers[]
  filterSlots?: FormAnswerMembers[]
  /** The source's core breakdowns; a core key not among them is a plain Cube member name. */
  breakdowns?: Record<string, BreakdownConfig>
  bucketing: BucketingState
}): ResolvedWidgetQuery => {
  const dimensions: string[] = []
  const filters: InsightsFilter[] = []
  const customBreakdowns: ResolvedCustomBreakdown[] = []
  let bucketedDimension: ResolvedBucketedDimension | null = null
  const dateBuckets: ResolvedDateBucket[] = []
  const dateGroupings: Record<string, DateGrouping> = {}
  const { spanFloorByMember } = bucketing.dates
  // A later date breakdown is floored one step coarser than the earlier one: date breakdowns lift the
  // chart's top-N limit, so floored only by their range two of them multiply into thousands of cells.
  let siblingFloor: TimeGranularity | null = null
  // R1: one displayed join per question, tracking which of its columns already carry a condition.
  const displayJoins = new Map<string, { members: FormAnswerMembers; usedColumns: Set<string> }>()

  for (const key of breakdownKeys) {
    // A numeric key is dropped whole — before it claims a join — when bucketing is off, so it renders
    // without that dimension instead of one bar per distinct value, and when the query already has its
    // one bucketed dimension.
    if (isNumericBreakdownKey(key, breakdowns) && (!bucketing.enabled || bucketedDimension != null)) continue
    if (!isCustomBreakdownKey(key)) {
      const core = coreBreakdownFor(key, breakdowns)
      if (core?.type === 'time' && bucketing.enabled) {
        // Only the widget's own time field is bounded by the range; another member is probed like a
        // question and, while its span is measured, trusts a saved pick.
        const grouping = resolveDateGrouping({
          saved: savedDateGranularity(bucketing.entries[key]),
          auto: spanAutoGranularity(bucketing.dates, key),
          legibleFloor: key in spanFloorByMember ? (spanFloorByMember[key] ?? null) : bucketing.dates.rangeFloor,
          siblingFloor,
        })
        dimensions.push(`${key}.${grouping.effective}`)
        dateBuckets.push({ dimensionIndex: dimensions.length - 1, rawMember: key, granularity: grouping.effective })
        dateGroupings[key] = grouping
        siblingFloor = oneStepCoarser(grouping.effective)
        continue
      }
      if (core?.type !== 'number') {
        dimensions.push(key)
        continue
      }
      // No pins — a core field sits on the view's own join path — and its values are in the org currency.
      const field: NumericBucketField = { stem: core.bucketStem, rawMember: core.sourceKey }
      const bucketed = resolveNumericBucket(field, bucketing.entries[key], bucketing.currency)
      dimensions.push(bucketed.dimension)
      bucketedDimension = { ...bucketed.bucketed, key, dimensionIndex: dimensions.length - 1 }
      continue
    }
    if (!formAnswers) continue
    const breakdown = parseCustomBreakdown(key)
    if (!breakdown) continue
    const isNumber = breakdown.questionType === 'number'
    const isDate = breakdown.questionType === 'date'
    // Like a number, a date question has no raw-value grouping worth drawing, so with bucketing off it
    // is dropped before it claims a join.
    if (isDate && !bucketing.enabled) continue
    const existing = displayJoins.get(questionKey(breakdown))
    const members = existing?.members ?? (displayJoins.size === 0 ? formAnswers : breakdownSlots?.[displayJoins.size - 1])
    if (!members) continue
    const field = isNumber ? numericBucketField(members) : null
    if (isNumber && field == null) continue
    const bucketed = field ? resolveNumericBucket(field, bucketing.entries[key], null) : null
    const valueMember = resolveValueMember(members, breakdown.valueMember, breakdown.level)
    const dateMember = isDate ? valueMember : null
    const dateGrouping =
      dateMember == null
        ? null
        : resolveDateGrouping({
            saved: savedDateGranularity(bucketing.entries[key]),
            auto: spanAutoGranularity(bucketing.dates, dateMember),
            // A saved pick has no span floor until the span is measured, so it draws as picked.
            legibleFloor: spanFloorByMember[dateMember] ?? null,
            siblingFloor,
          })
    const dimension =
      bucketed?.dimension ??
      (dateMember != null && dateGrouping != null ? `${dateMember}.${dateGrouping.effective}` : valueMember)
    // A repeated dimension (the same question at the same level in two rows) adds nothing.
    if (dimension == null || dimensions.includes(dimension)) continue
    if (!existing) {
      displayJoins.set(questionKey(breakdown), { members, usedColumns: new Set() })
      filters.push(...identityFiltersFor(breakdown, members))
    }
    dimensions.push(dimension)
    customBreakdowns.push({ breakdown, dimensionIndex: dimensions.length - 1, members })
    if (bucketed) bucketedDimension = { ...bucketed.bucketed, key, dimensionIndex: dimensions.length - 1 }
    if (dateMember != null && dateGrouping != null) {
      dateBuckets.push({ dimensionIndex: dimensions.length - 1, rawMember: dateMember, granularity: dateGrouping.effective })
      dateGroupings[key] = dateGrouping
      siblingFloor = oneStepCoarser(dateGrouping.effective)
    }
  }

  let nextSlot = 0
  const placeFilter = (filter: WidgetFilter, comparison: FilterComparison | null): void => {
    if (!isCustomBreakdownKey(filter.breakdown)) {
      filters.push(...(valueConditions(filter.breakdown, filter, comparison) ?? []))
      return
    }
    if (!formAnswers) return
    const custom = parseCustomFilter(filter.breakdown)
    if (!custom) return
    const conditionsOn = (members: FormAnswerMembers) => {
      const member = resolveValueMember(members, custom.rule.member, custom.level)
      return member == null ? null : valueConditions(member, filter, comparison)
    }
    // R3: the displayed join is already pinned to this question; only the value condition is new.
    const displayJoin = displayJoins.get(questionKey(custom))
    if (displayJoin && !displayJoin.usedColumns.has(columnKey(custom.level))) {
      const conditions = conditionsOn(displayJoin.members)
      if (conditions == null) return
      filters.push(...conditions)
      displayJoin.usedColumns.add(columnKey(custom.level))
      return
    }
    // R2: an existence check on its own pinned join.
    const slot = filterSlots?.[nextSlot]
    if (!slot) return
    const conditions = conditionsOn(slot)
    if (conditions == null) return
    nextSlot += 1
    filters.push(...identityFiltersFor(custom, slot))
    filters.push(...conditions)
  }
  // A filter whose values make a condition asks to narrow, and one that then places nowhere (a custom key with
  // no join to sit on) would answer for more records than it asked for, so it is reported for a caller to
  // refuse. One still missing its values makes no condition, and is a no-op rather than a failure.
  const unplacedFilters: string[] = []
  for (const filter of widgetFilters) {
    const comparison = filterComparison(filter.breakdown, breakdowns)
    const placedBefore = filters.length
    placeFilter(filter, comparison)
    const asksToNarrow = valueConditions(filter.breakdown, filter, comparison) != null
    if (asksToNarrow && filters.length === placedBefore) unplacedFilters.push(filter.breakdown)
  }

  return {
    dimensions,
    filters,
    unplacedFilters,
    customBreakdowns,
    bucketedDimension,
    dateBuckets,
    dateGroupings,
    overflow: resolveBucketOverflow(dimensions, filters, bucketedDimension),
  }
}

/** Capacity is spent per distinct question, not per key — two levels of one question share a join. */
export const countCustomBreakdowns = (breakdownKeys: string[]): number => {
  const questions = new Set<string>()
  let undecodable = 0
  for (const key of breakdownKeys) {
    if (!isCustomBreakdownKey(key)) continue
    const ref = parseCustomQuestion(key)
    if (ref) questions.add(questionKey(ref))
    else undecodable += 1
  }
  return questions.size + undecodable
}

type OfferedQuestion = { imported: boolean; questionType: CustomBreakdownType }

/** An imported dimension can be a breakdown only once its type has a value member to group by. */
export const canBreakdownBy = (breakdown: OfferedQuestion): boolean =>
  breakdown.imported && BREAKDOWN_TYPES[breakdown.questionType].member != null

/** Whether a stored key groups by a bucket dimension: a `number` question, or a core breakdown typed `number`. */
export const isNumericBreakdownKey = (key: string, breakdowns: Record<string, BreakdownConfig> | undefined): boolean =>
  isCustomBreakdownKey(key)
    ? decodeCustomBreakdown(key)?.questionType === 'number'
    : coreBreakdownFor(key, breakdowns)?.type === 'number'

/** Whether a stored key groups by date: a `date` question, or a core breakdown typed `time`. */
export const isDateBreakdownKey = (key: string, breakdowns: Record<string, BreakdownConfig> | undefined): boolean =>
  isCustomBreakdownKey(key)
    ? decodeCustomBreakdown(key)?.questionType === 'date'
    : coreBreakdownFor(key, breakdowns)?.type === 'time'

export const canFilterBy = (breakdown: OfferedQuestion): boolean =>
  breakdown.imported && BREAKDOWN_TYPES[breakdown.questionType].filter != null

export const breakdownTypeLabelKey = (questionType: CustomBreakdownType): string => BREAKDOWN_TYPES[questionType].labelKey

/** Whether a filter's stored values are option ids, which only its value list turns into the labels shown. */
export const filterStoresOptionIds = (filterKey: string): boolean => {
  const filter = parseCustomFilter(filterKey)
  return filter != null && (filter.level != null || filter.rule.member === 'optionId')
}

export type FilterValueDisplay = 'raw' | 'boolean' | 'optionLabel'

export type ResolvedFilterValuesQuery = {
  dimensions: string[]
  /** Aggregates the value list needs beyond its dimensions; empty unless a label has to be chosen. */
  measures: string[]
  /** Pins the value list to the one question the filter names. */
  identityFilters: DimensionFilter[]
  /** Dimensions to order ascending — keeps admission under the row cap deterministic. */
  orderBy: string[]
  display: FilterValueDisplay
}

/**
 * The Cube query shape that lists a filter's pickable values. A standard dimension is its own
 * domain; a custom question must be pinned to its identity, or every question's answers would pool
 * into one list. id-keyed types pair the option id (the stored filter value) with the answered
 * label (the display), so the list survives options that share a label or get renamed. They also
 * carry a recency measure, which decides which of a renamed option's labels the picker shows.
 */
export const resolveFilterValuesQuery = (
  filterKey: string,
  formAnswers?: FormAnswerBaseMembers
): ResolvedFilterValuesQuery | null => {
  if (!isCustomBreakdownKey(filterKey)) {
    return { dimensions: [filterKey], measures: [], identityFilters: [], orderBy: [], display: 'raw' }
  }
  const filter = parseCustomFilter(filterKey)
  if (!filter || !formAnswers) return null
  const identityFilters = identityFiltersFor(filter, formAnswers)
  if (filter.level != null) {
    const pathLevel = formAnswers.pathLevels?.[filter.level - 1]
    if (!pathLevel) return null
    return {
      dimensions: [pathLevel.optionId, pathLevel.label],
      measures: [formAnswers.latestSubmittedOn],
      identityFilters,
      orderBy: [pathLevel.optionId, pathLevel.label],
      display: 'optionLabel',
    }
  }
  // A compared filter types its operands in; there is no value list to offer.
  if (filter.rule.kind === 'compare') return null
  if (filter.rule.member === 'optionId') {
    return {
      dimensions: [formAnswers.optionId, formAnswers.value],
      measures: [formAnswers.latestSubmittedOn],
      identityFilters,
      orderBy: [formAnswers.optionId, formAnswers.value],
      display: 'optionLabel',
    }
  }
  const member = formAnswers[filter.rule.member]
  return {
    dimensions: [member],
    measures: [],
    identityFilters,
    orderBy: [member],
    display: filter.rule.member === 'valueBool' ? 'boolean' : 'raw',
  }
}
